import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import {
  useActiveLanguageIsUnseeded,
  useLanguageCode,
} from "../lib/activeLanguage";
import type { LanguageCode } from "../lib/languages";
import { requireAuth } from "../lib/requireAuth";
import { UnseededLanguageLanding } from "../lib/UnseededLanguageLanding";
import { FilterSelect, type FilterOption } from "../components/FilterSelect";
import { FilterBar, FilterEmpty } from "../components/FilterBar";
import { SearchInput } from "../components/SearchInput";
import { PageEyebrow } from "../components/PageEyebrow";
import { Centered } from "../components/Centered";

export const Route = createFileRoute("/constructions")({
  beforeLoad: ({ context, location }) =>
    requireAuth(context.queryClient, location.href),
  component: ConstructionsPage,
});

type CefrTag = {
  source: string;
  level: string;
};

type Construction = {
  id: number;
  language: string;
  form: string;
  description: string | null;
  meta: Record<string, unknown>;
  cefrTags: CefrTag[];
};

type ConstructionPage = {
  items: Construction[];
  total: number;
  limit: number;
  offset: number;
};

async function fetchConstructions(
  language: LanguageCode,
): Promise<ConstructionPage> {
  const { data, error } = await api.GET("/constructions", {
    params: { query: { language, limit: 200 } },
  });
  if (error) throw error;
  return data as unknown as ConstructionPage;
}

function ConstructionsPage() {
  const language = useLanguageCode();
  const unseeded = useActiveLanguageIsUnseeded();
  const [functionFilter, setFunctionFilter] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["constructions", language],
    queryFn: () => fetchConstructions(language),
    // Same logic as lexicon — content-level data, doesn't shift mid-
    // session.
    staleTime: 5 * 60_000,
    enabled: !unseeded,
  });

  const functionOptions = useMemo<FilterOption[]>(() => {
    const fns = new Set<string>();
    for (const c of data?.items ?? []) {
      const fn =
        typeof c.meta.function === "string" ? c.meta.function : "other";
      fns.add(fn);
    }
    return [...fns]
      .sort((a, b) => a.localeCompare(b))
      .map((fn) => ({ value: fn, label: humanizeFunction(fn) }));
  }, [data?.items]);

  if (unseeded) return <UnseededLanguageLanding />;
  if (isLoading)
    return <Centered width="wide">Loading constructions…</Centered>;
  if (isError || !data)
    return <Centered width="wide">Could not load constructions.</Centered>;
  if (data.total === 0) {
    return (
      <Centered width="wide">
        <p className="text-ink-700 mb-2">No constructions seeded yet.</p>
        <p className="text-ink-400 text-sm">
          Run{" "}
          <code className="bg-ink-100 px-1 rounded">
            uv run python -m lingua.seed.full
          </code>{" "}
          to populate.
        </p>
      </Centered>
    );
  }

  const needle = search.trim().toLowerCase();
  const matchesSearch = (c: Construction) =>
    !needle ||
    c.form.toLowerCase().includes(needle) ||
    (c.description ?? "").toLowerCase().includes(needle);

  // Group by `function` meta field when present (greeting / farewell / apology / ...)
  const groups = new Map<string, Construction[]>();
  for (const c of data.items) {
    const fn = typeof c.meta.function === "string" ? c.meta.function : "other";
    if (functionFilter && fn !== functionFilter) continue;
    if (!matchesSearch(c)) continue;
    const bucket = groups.get(fn) ?? [];
    bucket.push(c);
    groups.set(fn, bucket);
  }
  const sortedGroups = [...groups.entries()].sort((a, b) =>
    a[0].localeCompare(b[0]),
  );
  const shownCount = sortedGroups.reduce((n, [, items]) => n + items.length, 0);

  return (
    <section className="mx-auto max-w-4xl px-4 sm:px-6 py-8 sm:py-12">
      <PageEyebrow
        label="Reference"
        mono={`${language}.constructions`}
        count={`${data.total} ${data.total === 1 ? "phrase" : "phrases"}`}
      />
      <h2 className="font-serif text-3xl text-ink-900 mb-2 tracking-tight">
        Constructions
      </h2>
      <p className="text-ink-500 text-sm mb-5 sm:mb-6 max-w-2xl">
        Useful phrases — expressions whose meaning is bigger than the sum of
        their words. Grouped by what you'd use them to do.
      </p>
      <div className="hairline mb-6" />

      <FilterBar
        shown={shownCount}
        total={data.total}
        onClear={
          functionFilter || search
            ? () => {
                setFunctionFilter("");
                setSearch("");
              }
            : undefined
        }
      >
        <FilterSelect
          layout="inline"
          label="Function"
          value={functionFilter}
          onChange={setFunctionFilter}
          options={functionOptions}
          placeholder="All functions"
        />
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="form or description…"
          widthClass="w-48"
        />
      </FilterBar>

      {shownCount === 0 && <FilterEmpty noun="constructions" />}

      {sortedGroups.map(([fn, items]) => (
        <div key={fn} className="mb-8">
          <div className="mb-3 flex items-baseline gap-3">
            <h3 className="font-serif text-lg text-ink-900">
              {humanizeFunction(fn)}
            </h3>
            <span className="font-mono text-[11px] text-ink-400 tabular-nums">
              {items.length}
            </span>
          </div>
          <ul className="divide-y divide-ink-100 border border-ink-100 bg-paper">
            {items.map((c) => (
              <ConstructionCard key={c.id} construction={c} />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function ConstructionCard({ construction: c }: { construction: Construction }) {
  const register =
    typeof c.meta.register === "string" ? (c.meta.register as string) : null;
  const context =
    typeof c.meta.context === "string" ? (c.meta.context as string) : null;

  return (
    <li className="p-3 sm:p-4 hover:bg-ink-50 transition-colors">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-1">
        <span className="font-serif text-lg text-ink-900 min-w-0">
          {c.form}
        </span>
        {/* Metadata reads as a string of citations, not as state pills —
            §7 reserves emerald/amber for mastery state, so CEFR + register
            + context get the mono-ink address treatment instead. */}
        <div className="flex gap-2 text-xs flex-wrap sm:justify-end items-center">
          {(c.cefrTags ?? []).map((t) => (
            <span
              key={`${t.source}-${t.level}`}
              className="font-mono tabular-nums text-ink-500"
              title={`${t.source} curriculum`}
            >
              {t.level}
            </span>
          ))}
          {register && (
            <span className="border border-ink-200 px-1.5 py-0.5 text-ink-500 text-[11px]">
              {register}
            </span>
          )}
          {context && (
            <span className="border border-ink-200 px-1.5 py-0.5 text-ink-500 text-[11px]">
              {context.replace(/_/g, " ")}
            </span>
          )}
        </div>
      </div>
      {c.description && (
        <p className="text-sm text-ink-700 leading-relaxed">{c.description}</p>
      )}
    </li>
  );
}

// Group slugs like `apology_or_attention` get sentence-cased, and the
// few that read as "noun-then-modifier" in the seed file get rewritten
// to read like natural English headings rather than ALL CAPS shouts.
function humanizeFunction(slug: string): string {
  const overrides: Record<string, string> = {
    affirmation_obvious: "Affirmation — when it's obvious",
    apology: "Apology",
    apology_or_attention: "Apology / getting attention",
    comprehension_fail: "Saying you didn't understand",
    existence: "Saying something exists (there is / there are)",
    greeting: "Greetings",
    parting: "Saying goodbye",
    politeness: "Polite phrases",
    request: "Requests",
    thanks: "Thanks",
    introduction: "Introducing yourself",
  };
  if (slug in overrides) return overrides[slug] as string;
  const spaced = slug.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
